import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useError } from '../../store/reducers/error';
import { useObserveScroll } from '../../helpers/use-observe-scroll';
import throttle from 'lodash.throttle';

interface IDispatchProps {
  loadMore: (page?: number) => void;
}

interface IOwnProps {
  minBodyHeight?: number;
  reset?: boolean;
  error?: boolean;
  children: React.ReactNode;
  libraryCards?: any[];
  hasMoreItems?: boolean;
  infiniteButton?: boolean;
  infiniteButtonEnable?: boolean;
}

interface IProps extends IOwnProps, IDispatchProps {}

const defaultProps: Partial<IProps> = {
  minBodyHeight: 0,
  hasMoreItems: false,
  libraryCards: [],
};

export const InfiniteScroll: React.FC<IProps> = props => {
  const {
    minBodyHeight,
    loadMore,
    children,
    libraryCards,
    hasMoreItems,
    reset,
    infiniteButtonEnable,
  } = {
    ...defaultProps,
    ...props,
  };

  const [isAtPageBottom, setIsAtPageBottom] = useState(false);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [buttonClick, setButtonClicked] = useState(false);
  const [page, setPage] = useState(1);
  const { error } = useError();
  const loadMoreCards = useCallback(() => {
    loadMore(page);
    setScrollLoading(true);
  }, [loadMore, page]);

  const setButton = throttle(() => {
    setButtonClicked(true);
  }, 500);

  useObserveScroll(setIsAtPageBottom);

  const libraryCardsLength = libraryCards?.length;

  useEffect(() => {
    setIsAtPageBottom(false);
    setScrollLoading(false);
    setButtonClicked(false);
    setPage(p => (reset ? 2 : p + 1));
  }, [libraryCardsLength, reset]);
  useEffect(() => {
    const hasMinBodyHeight = document.body.offsetHeight > minBodyHeight;
    if (
      hasMinBodyHeight &&
      !scrollLoading &&
      hasMoreItems &&
      isAtPageBottom &&
      !error &&
      !infiniteButtonEnable
    ) {
      loadMoreCards();
    }
  }, [
    isAtPageBottom,
    error,
    hasMoreItems,
    minBodyHeight,
    loadMoreCards,
    scrollLoading,
    infiniteButtonEnable,
  ]);

  useEffect(() => {
    if (
      !scrollLoading &&
      hasMoreItems &&
      !error &&
      infiniteButtonEnable &&
      buttonClick
    ) {
      loadMoreCards();
    }
  }, [
    error,
    hasMoreItems,
    loadMoreCards,
    scrollLoading,
    infiniteButtonEnable,
    buttonClick,
  ]);

  return (
    <>
      {React.Children.map(children, (child, index) => {
        return React.cloneElement(child as ReactElement, {
          index,
          scrollLoading,
          libraryCards,
          hasMoreItems,
          error,
          infiniteButtonEnable,
          infiniteButton: setButton,
        });
      })}
    </>
  );
};

export default InfiniteScroll;
